import { Translate } from 'next-translate';
import { createRoot } from 'react-dom/client';
import { Tooltip } from '@arkestro/arkestro-design-system';
import { InfoCircleOutlined, TeamOutlined } from '@ant-design/icons';

import { CustomColumnHeader } from 'types';

import styles from '@components/bid_request/details/LineItemsTable/LineItemsTable.module.scss';

const ICON_CLASS_NAME = 'supplier-icon-wrapper';
const INFO_ICON_CLASS_NAME = 'info-icon-wrapper';

interface ModifyColHeaderIconParams {
  TH: HTMLTableCellElement;
  header?: CustomColumnHeader;
  hideFromSupplier?: boolean;
  infoTooltip?: string;
  rowIndex?: number;
  showInfoIcon?: boolean;
  t: Translate;
}

/**
 * This function modifies the column header to display or remove the hidden icon
 * based on the visibility of the supplier column, and optionally renders an info
 * tooltip icon for buyer-only columns (e.g. the negotiation_starting_price and price columns).
 *
 * @param params - Parameters for modifying the column header.
 * @param params.TH - The table header cell element to modify.
 * @param params.header - The custom column header object.
 * @param params.hideFromSupplier - Forces the hidden icon for system columns that have no
 * `CustomColumnHeader` (e.g. price columns), which are always buyer-only. Ignored when `header`
 * is present — `header.is_supplier_visible` takes precedence in that case.
 * @param params.infoTooltip - Optional tooltip text to render an info icon after the column title.
 * @param params.rowIndex - The index of the row (used to determine if it's an alpha header).
 * @param params.t - The translation function for internationalization.
 */
export const modifyColHeaderIcon = ({
  TH,
  header,
  hideFromSupplier,
  infoTooltip,
  rowIndex,
  showInfoIcon,
  t,
}: ModifyColHeaderIconParams): void => {
  // Render info tooltip icon for buyer-only system columns (e.g. Negotiation Starting Price, Reporting Price).
  // Handled independently so a column can have both an info icon and a supplier visibility icon.
  //
  // Every info icon reveals its text on HOVER. The NSB price columns previously used a click-to-open
  // popover, which was inconsistent with every other column header and needed a click for text that
  // is purely informational — the per-lot override control it once wrapped no longer exists.
  if ((infoTooltip || showInfoIcon) && rowIndex === 1) {
    const colHeader = TH.querySelector('.colHeader');
    if (colHeader) {
      // Mount inside .colHeader so it stays inline with the title text,
      // not in the flex parent where it would be pushed right by the dropdown button.
      if (!colHeader.querySelector(`.${INFO_ICON_CLASS_NAME}`)) {
        const infoContainer = document.createElement('span');
        infoContainer.className = INFO_ICON_CLASS_NAME;
        colHeader.appendChild(infoContainer);
        const root = createRoot(infoContainer);
        root.render(
          <Tooltip title={infoTooltip}>
            <InfoCircleOutlined className={styles.columnInfoIcon} />
          </Tooltip>
        );
      }
    }
  }

  const isHiddenFromSupplier = header ? !header.is_supplier_visible : Boolean(hideFromSupplier);
  if (!isHiddenFromSupplier) {
    const iconContainer = TH.querySelector(`.${ICON_CLASS_NAME}`);

    if (iconContainer) {
      // Remove the hidden icon
      iconContainer.remove();
    }
    return;
  }
  // Don't modify alpha headers (A, B, C, etc.)
  if (rowIndex !== 1) return;

  const colHeader = TH.querySelector('.colHeader');
  if (!colHeader || !colHeader.parentNode) return;

  // Create a wrapper div to mount React element to
  const iconContainer = document.createElement('span');
  iconContainer.className = ICON_CLASS_NAME;

  // Prevent duplicate icons on re-render
  if (colHeader.parentNode.querySelector(`.${ICON_CLASS_NAME}`)) return;
  // The header container is a flex row, so DOM order sets the layout: insert after the header
  // text so the icon sits flush right, immediately left of the dropdown trigger when the column
  // has one (insertBefore with a null reference node appends to the end).
  const dropdownButton = colHeader.parentNode.querySelector('button.changeType');
  colHeader.parentNode.insertBefore(iconContainer, dropdownButton);

  // Build the React component and render it
  const root = createRoot(iconContainer);
  root.render(
    <Tooltip
      overlayClassName={styles.supplierHiddenTooltip}
      title={t('bid_request.line_items.tooltips.not_visible_column')}
    >
      <TeamOutlined className={styles.supplierHiddenIcon} />
    </Tooltip>
  );
};
